import _ from 'lodash';
import moment from 'moment-timezone';
import { Link } from 'react-router-dom';

import { Tag, Tooltip } from 'antd';

import { CurrentUserStore, IssueStore, ShopStore } from 'stores';

import {
  getRoutineDueDate,
  getRoutineDueEngineHours,
  getRoutineDueMileage,
} from 'containers/IssueProfile/share/utils';
import { calcMileage, convertSecondsToHours } from 'helpers/unitCalculations';
import { customFleets } from 'shared';
import { showDemoVinOrRealVin } from 'shared/utils';
import { WarningTwoTone } from '@ant-design/icons';
import { useState, useEffect } from 'react';

export const getGroupingRoutineService = (data) => {
  const getCategory = (name) => {
    if (
      name.indexOf('Inspect') !== -1 ||
      name.indexOf('inspect') !== -1 ||
      name.indexOf('Check') !== -1 ||
      name.indexOf('check') !== -1
    )
      return 'Inspect';

    if (name.indexOf('Replace') !== -1 || name.indexOf('replace') !== -1)
      return 'Replace';

    if (name.indexOf('Lubricate') !== -1 || name.indexOf('lubricate') !== -1)
      return 'Lubricate';

    if (name.indexOf('Change') !== -1 || name.indexOf('change') !== -1)
      return 'Change';

    return 'Other';
  };

  const getCategoryIndex = (category) => {
    switch (category) {
      case 'Inspect':
        return 1;
      case 'Lubricate':
        return 2;
      case 'Replace':
        return 3;
      case 'Change':
        return 4;
      case 'Other':
        return 5;
      default:
        return 0;
    }
  };

  let _data = Object.values(
    _.reduce(
      data,
      (prev, el) => {
        let issue = IssueStore.data.get(el);
        let category = getCategory(issue.getName());

        if (prev[category] === undefined) {
          return {
            ...prev,
            [category]: {
              index: getCategoryIndex(category),
              category,
              items: [el],
              key: category,
            },
          };
        } else {
          prev[category].items.push(el);
          return prev;
        }
      },
      {}
    )
  );

  _data = _.sortBy(_data, ['index']);

  return _data;
};

export const getVMRSCode = (item) => {
  let code;

  switch (item) {
    case 'Battery':
      code = '031';
      break;
    case 'Brakes':
      code = '013';
      break;
    case 'DEF fluid':
      code = '043';
      break;
    case 'Air Flow':
      code = '041';
      break;
    case 'ESR':
      code = '031';
      break;
    case 'fuel trim':
      code = '033';
      break;
    case '"cranking voltage':
      code = '032';
      break;
    case 'starter':
      code = '032';
      break;
    default:
      code = '900';
  }

  return code;
};

export const getVMRSText = (item) => {
  let text;

  switch (item) {
    case 'Battery':
      text = 'CRANKING SYSTEM';
      break;
    case 'Brakes':
      text = 'BRAKE SYSTEM';
      break;
    case 'DEF fluid':
      text = 'EXHAUST SYSTEM';
      break;
    case 'Air Flow':
      text = 'AIR INTAKE SYSTEM';
      break;
    default:
      text = 'OTHER';
  }

  return text;
};

export const convertVMRSCode = (n) => {
  if (!n) return '900';
  if (n / 10 < 1) return `00${n}`;
  if (n / 10 > 10 && n / 10 < 100) return n;

  return `0${n}`;
};

// get the escalation info for an issue
const EscalatedInfo = ({ issueId }) => {
  const [escalatedDetails, setEscalatedDetails] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      const data = await IssueStore.getEscalatedDetails(issueId);
      setEscalatedDetails(data);
      console.log(data, 'escalatedDetails');
    };
    fetch();
  }, [issueId]);

  if (!escalatedDetails) return <span>Loading...</span>;

  let date = new Date(escalatedDetails[0]?.escalated_at);
  const time = date.toLocaleTimeString('en-US', {
    timeZone: CurrentUserStore.user.settings.timezone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  const datePart = date.toLocaleDateString('en-US', {
    timeZone: CurrentUserStore.user.settings.timezone,
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const formattedDate = `${time}, ${datePart}`;
  return <span>{formattedDate || 'No escalation info'}</span>;
};

export const getColumnsBySource = (source, page = null) => {
  let columns = [
    {
      title: 'Priority',
      key: 'priority',
      width: 100,
      sorter: true,
      render: (issue) => {
        const showEscalatedIssue = issue.action === 'Perform';
        const priorityTagColor = issue.priorityTagColor;
        return (
          <div
            style={{
              position: 'relative',
              display: 'inline-block',
              width: 'fit-content',
            }}
          >
            <Tag color={priorityTagColor}>{issue.priorityCategory}</Tag>
            {showEscalatedIssue && (
              <WarningTwoTone
                twoToneColor={priorityTagColor}
                style={{
                  position: 'absolute',
                  top: '0%',
                  right: '0%',
                  transform: 'translate(10%, -55%)',
                  fontSize: '20px',
                }}
              />
            )}
            {showEscalatedIssue && (
              <Tooltip
                placement="right"
                title={
                  <span>
                    • Escalated at <EscalatedInfo issueId={issue.id} />
                    {/* <br />
                    • Cause: causeMessage */}
                  </span>
                }
              >
                <div
                  style={{
                    fontFamily: 'Roboto, sans-serif',
                    fontWeight: 400,
                    fontStyle: 'normal',
                    fontSize: '12px',
                    lineHeight: 1,
                    letterSpacing: 0,
                    textDecoration: 'underline',
                    textDecorationStyle: 'solid',
                    textUnderlineOffset: 1,
                    textDecorationThickness: 'auto',
                    color: priorityTagColor,
                    marginTop: '10px',
                    marginLeft: '2px',
                  }}
                >
                  Escalated
                </div>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: 'Source',
      key: 'source',
      render: (issue) => issue.source,
    },
    {
      title: 'Issue Name',
      key: 'name',
      render: (issue) => {
        const { description, item } = issue;
        return (
          <span>
            {item && (
              <>
                <strong>{item}</strong>
                <br />
              </>
            )}
            {description}
          </span>
        );
      },
    },
  ];

  if (page !== 'carPage') {
    columns.push({
      title: 'Unit',
      key: 'carName',
      sorter: true,
      render: (issue) => {
        const { name, vin, id, engineHours, mileageTotal } = issue.car;

        return (
          <>
            <Link to={`/car/${id}`} onClick={(e) => e.stopPropagation()}>
              {name ? name : 'N/A'} <br />
              {vin ? showDemoVinOrRealVin(vin) : 'N/A'} <br />
            </Link>

            <>
              {!_.isNaN(engineHours) &&
              !customFleets.motiveFleets.includes(ShopStore.currentShop.id)
                ? `${Math.round(
                    convertSecondsToHours(engineHours)
                  ).toReadable()} engine hours`
                : ''}

              <br />

              {!_.isNaN(mileageTotal) &&
              !customFleets.motiveFleets.includes(ShopStore.currentShop.id)
                ? `${Math.round(calcMileage(mileageTotal)).toReadable()} ${
                    CurrentUserStore.user.displayedOdometer
                  }`
                : ''}
            </>
          </>
        );
      },
    });
  }

  columns = [
    ...columns,
    {
      title: 'Trigger Time',
      key: 'firstTriggeredAt',
      sorter: true,
      render: (issue) => {
        const firstTriggeredAtDate =
          issue?.firstTriggeredAt ||
          issue?.report?.date ||
          issue?.createdAt ||
          issue?.reportDate;

        const formattedFirstTriggeredAtDate = moment
          .tz(firstTriggeredAtDate, CurrentUserStore.user.settings.timezone)
          .format('h:mmA, Do MMM YYYY');

        let lastTriggeredAt;

        if (issue.status === 'done' && issue?.doneDate) {
          lastTriggeredAt = issue?.doneDate;
        } else {
          lastTriggeredAt =
            issue?.lastTriggeredAt ||
            issue?.report?.date ||
            issue?.updatedAt ||
            issue?.reportDate;
        }

        const formattedLastTriggeredAtDate = moment
          .tz(lastTriggeredAt, CurrentUserStore.user.settings.timezone)
          .format('h:mmA, Do MMM YYYY');

        if (
          (issue?.triggerCount || 1) === 1 ||
          formattedFirstTriggeredAtDate === formattedLastTriggeredAtDate
        ) {
          return (
            <>
              <strong>Last -</strong> {formattedLastTriggeredAtDate}
            </>
          );
        }

        return (
          <>
            <strong>First -</strong> {formattedFirstTriggeredAtDate}
            <br />
            <strong>Last -</strong> {formattedLastTriggeredAtDate}
          </>
        );
      },
    },
    // {
    //   title: 'Last Triggered At',
    //   key: 'lastTriggeredAt',
    //   render: (issue) => {
    //     if (issue.status === 'done' && issue?.doneDate) {
    //       return moment
    //         .tz(issue?.doneDate, CurrentUserStore.user.settings.timezone)
    //         .format('lll');
    //     }

    //     const updatedAt =
    //       issue?.lastTriggeredAt ||
    //       issue?.report?.date ||
    //       issue?.updatedAt ||
    //       issue?.reportDate;

    //     return moment
    //       .tz(updatedAt, CurrentUserStore.user.settings.timezone)
    //       .format('lll');
    //   },
    // },
    {
      title: 'Trigger Count',
      key: 'triggerCount',
      render: (issue) => {
        return issue?.triggerCount || 1;
      },
    },
  ];

  if (!_.includes(['all'], source)) {
    columns = _.filter([...columns], (col) => col.key !== 'source');
  }

  if (!_.includes(['all', 'dtc'], source)) {
    columns = _.filter([...columns], (col) => col.key !== 'activeTime');
  }

  if (source === 'routine' || source === 'all') {
    columns = [
      ...columns,
      {
        title: 'Due',
        key: 'dueDate',
        sorter: true,
        render: (issue) => {
          if (issue.routineInfo) {
            let messages = [];

            if (getRoutineDueDate(issue))
              messages.push(
                <>
                  <b>On Date: </b>
                  {`${getRoutineDueDate(issue)}`}
                </>
              );

            if (getRoutineDueEngineHours(issue))
              messages.push(
                <>
                  <b>On Engine Hours:</b>{' '}
                  {`${Number(getRoutineDueEngineHours(issue)).toReadable()}`}{' '}
                  engine hours
                </>
              );

            if (getRoutineDueMileage(issue))
              messages.push(
                <>
                  <b>On Mileage: </b>{' '}
                  {`${Number(getRoutineDueMileage(issue)).toReadable()} ${
                    CurrentUserStore.user.displayedOdometer
                  }`}
                </>
              );

            if (_.isEmpty(messages)) {
              return 'N/A';
            }

            return _.map(messages, (msg) => (
              <>
                {msg}
                <br />
              </>
            ));
          }
          return 'N/A';
        },
      },
    ];
  }

  return _.filter(columns, Boolean);
};

export const convertSource = (source) => {
  if (_.isNil(source)) return 'all';

  if (source === 'faultCode') return 'dtc';

  if (source === 'pitstopSuggested') return 'algorithm';

  return source;
};
